import { useEffect, useId, useRef, useState, type RefObject } from 'react'
import { hostError, hostsApi, type HostCommand } from '../api'
import { t } from '../i18n'
import { Button, CodeCells, Dialog, Icon } from '../ui'
import { codeOf, codeText, one, outcome, refusalText, tn, type SwitchService } from './network/nodeSwitch'

function requestId(): string {
  try {
    return crypto.randomUUID()
  } catch {
    return `r${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`
  }
}

/*
 * Restart node, Update logic and Update agent (HOLD-A8): the host agent runs the
 * switch, the center passes its result back. ConfirmDialog shape: 440 px, the code
 * in six cells, focus on Cancel, no typed word. The action is a plain button, not
 * danger and not primary: the broadcast stays on air (09-voice "Console · planned
 * node restart", AUDIT GR-01, PNU-01, NSW-01).
 *
 * A finished switch (ok, or failed with the agent's data: rolled back, rollback
 * failed) closes the dialog and goes to onDone — the caller shows the toast and
 * the event. A refusal before anything changed stays here, in words.
 */

const CODE_TRIES = 5

const emptyCode = () => Array<string>(6).fill('')
const codeOfCells = (cells: string[]) => cells.join('')
const codeReady = (cells: string[]) => cells.every((c) => c.length === 1)

/** testid prefixes, one per action */
export const SWITCH_ID: Record<SwitchService, string> = { restart: 'restart-node', logic: 'update-logic', agent: 'update-agent' }

const LABEL: Record<SwitchService, Parameters<typeof t>[0]> = { restart: 'node.restart', logic: 'node.updateLogic', agent: 'node.updateAgent' }
const BUSY: Record<SwitchService, Parameters<typeof t>[0]> = { restart: 'node.busy.restart', logic: 'node.busy.logic', agent: 'node.busy.agent' }

export function SwitchDialog({
  service,
  hostId,
  name,
  releaseNo,
  streams,
  from,
  to,
  returnFocus,
  onClose,
  onDone,
  onRefused,
}: {
  service: SwitchService
  hostId: string
  name: string
  releaseNo?: number
  streams: number
  /** the component's version now and in the center's release (logic, agent) */
  from?: string
  to?: string
  returnFocus: RefObject<HTMLButtonElement | null>
  onClose: () => void
  /** the switch ran: ok, rolled back, or the rollback failed */
  onDone: (c: HostCommand) => void
  /** nothing changed; the sentence shown in the dialog */
  onRefused?: (text: string) => void
}) {
  const p = SWITCH_ID[service]
  const titleId = useId()
  const cancelRef = useRef<HTMLButtonElement>(null)
  const [cells, setCells] = useState(emptyCode)
  const [busy, setBusy] = useState(false)
  const [tries, setTries] = useState(CODE_TRIES)
  const [codeError, setCodeError] = useState('')
  const [formError, setFormError] = useState('')

  const closeRef = useRef(onClose)
  const backRef = useRef(returnFocus)
  closeRef.current = onClose
  backRef.current = returnFocus
  useEffect(() => {
    cancelRef.current?.focus()
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        closeRef.current()
        return
      }
      if (e.key !== 'Tab') return
      const root = document.getElementById(`${titleId}-title`)?.closest('[role="alertdialog"]')
      if (!root) return
      const items = [...root.querySelectorAll<HTMLElement>('button, input, a')].filter((el) => !el.hasAttribute('disabled'))
      const first = items[0]
      const last = items[items.length - 1]
      if (!first || !last) return
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault()
        first.focus()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('keydown', onKey)
      backRef.current.current?.focus()
    }
  }, [titleId])

  const refuse = (text: string) => {
    setFormError(text)
    onRefused?.(text)
  }

  const submit = async () => {
    if (!codeReady(cells) || busy || tries <= 0) return
    setBusy(true)
    setCodeError('')
    setFormError('')
    try {
      const c = await hostsApi.command(hostId, {
        kind: service === 'restart' ? 'restart_node' : 'update',
        ...(service === 'restart' ? {} : { args: { service } }),
        totp: codeOfCells(cells),
        request_id: requestId(),
      })
      const ran = c.state === 'ok' || (c.state === 'failed' && !!c.data && (c.data.rolled_back === true || c.data.failed === true))
      if (ran) {
        onDone(c)
        return
      }
      if (c.state === 'timeout') {
        refuse(t('node.timeout'))
        return
      }
      refuse(outcome(service, c, { name, release: releaseNo, streams, version: to }).text)
    } catch (e) {
      const h = hostError(e)
      if (h?.code === 'totp_invalid') {
        const left = tries - 1
        setTries(left)
        setCodeError(left > 0 ? t(one(left, 'node.wrongCode.one', 'node.wrongCode.many'), { n: left }) : t('node.locked'))
        return
      }
      if (h?.code === 'totp_locked') {
        setTries(0)
        setCodeError(h.retry_after_s ? t('node.lockedFor', { n: Math.ceil(h.retry_after_s / 60) }) : t('node.locked'))
        return
      }
      const head = service === 'restart' ? t('node.didNotRun.restart') : t(service === 'logic' ? 'node.didNotRun.logic' : 'node.didNotRun.agent', { name })
      const why = refusalText(h) ?? (h?.error ? codeText(codeOf(h.error), h.error) : '')
      refuse(why ? `${head} ${why}` : head)
    } finally {
      setBusy(false)
    }
  }

  const release = releaseNo !== undefined ? releaseNo : '—'
  const title = service === 'restart' ? tn('node.confirm.restart', { name, n: release }) : tn(service === 'logic' ? 'node.confirm.logic' : 'node.confirm.agent', { name })

  return (
    <Dialog
      id={titleId}
      modal
      busy={busy}
      testId={`${p}-dialog`}
      title={title}
      footer={
        <>
          <Button ref={cancelRef} variant="ghost" data-testid={`${p}-cancel`} onClick={onClose}>
            {t('node.cancel')}
          </Button>
          <Button variant="default" busy={busy} disabled={!codeReady(cells) || tries <= 0} data-testid={`${p}-confirm`} onClick={() => void submit()}>
            {busy ? t(BUSY[service]) : t(LABEL[service])}
          </Button>
        </>
      }
    >
      <ul data-testid={`${p}-facts`}>
        {service === 'agent' ? (
          <>
            {from && to && <li>{tn('node.agentPair', { from, to })}</li>}
            <li>{t('node.agentKeeps')}</li>
            <li>{t('node.agentBack')}</li>
          </>
        ) : (
          <>
            <li>{tn(one(streams, 'node.onAir.one', 'node.onAir.many'), { n: streams })}</li>
            <li>{tn('node.sockets', { n: 0 })}</li>
            <li>{tn('node.pauseTarget', { n: 0 })}</li>
            {service === 'restart' ? <li>{tn('node.releaseStays', { n: release })}</li> : from && to && <li>{tn('node.logicPair', { from, to })}</li>}
          </>
        )}
      </ul>
      <CodeCells id={`${p}-code`} label={t('node.code')} value={cells} invalid={!!codeError} onChange={setCells} />
      {codeError && (
        <span className="flex items-center gap-1.5 text-xs text-fail" role="alert" data-testid={`${p}-code-error`}>
          <Icon name="state-fail" size={12} />
          {codeError}
        </span>
      )}
      {formError && (
        <p className="text-[13px] text-text" role="alert" data-testid={`${p}-error`}>
          {formError}
        </p>
      )}
    </Dialog>
  )
}

/** Restart node: the switch of the release already installed. */
export function RestartNodeDialog(props: Omit<Parameters<typeof SwitchDialog>[0], 'service'>) {
  return <SwitchDialog service="restart" {...props} />
}

